import { useEffect } from "react";
import { useAddressGenerator } from "../hooks";
import { useOnboard } from "../hooks/useOnboard";
import type { ChainType } from "../sdk/types";

// Chain type display names and descriptions
const CHAIN_TYPE_OPTIONS: Array<{
  value: ChainType;
  label: string;
  description: string;
  example: string;
}> = [
  {
    value: "ethereum",
    label: "Ethereum",
    description: "Ethereum addresses (0x...)",
    example: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb",
  },
  {
    value: "bitcoin_taproot",
    label: "Bitcoin Taproot",
    description: "Bitcoin Taproot addresses (bc1p...)",
    example: "bc1p5d7rjq7g6rdk2yhzks9smlaqtedr4dekq08ge8ztwac36sfj9ugtg7eskl",
  },
  {
    value: "bitcoin_segwit",
    label: "Bitcoin SegWit",
    description: "Bitcoin SegWit addresses (bc1q...)",
    example: "bc1qw508d6qejxtdg4y5r3zarvary0c5xw7kv8f3t4",
  },
  {
    value: "bitcoin_legacy",
    label: "Bitcoin Legacy",
    description: "Bitcoin Legacy addresses (1...)",
    example: "1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa",
  },
  {
    value: "bitcoin_nested_segwit",
    label: "Bitcoin Nested SegWit",
    description: "Bitcoin Nested SegWit addresses (3...)",
    example: "3J98t1WpEZ73CNmQviecrnyiWrnqRhWNLy",
  },
  {
    value: "solana",
    label: "Solana",
    description: "Solana addresses (Base58)",
    example: "9WzDXwBbmkg8ZTbNMqUxvQRAyrZzDsGYdLVL9zYtAWWM",
  },
];

function AddressGenerator(): JSX.Element {
  const { setAccountData } = useOnboard();
  const {
    mnemonic,
    setMnemonic,
    addressIndex,
    setAddressIndex,
    chainType,
    setChainType,
    isMainnet,
    setIsMainnet,
    result,
    loading,
    generateAddress,
    isReady,
  } = useAddressGenerator();

  // Save generated address and mnemonic to useOnboard (single source of truth) when generation succeeds
  // Only save Ethereum addresses for onboarding compatibility
  useEffect(() => {
    if (
      result?.success &&
      result.address &&
      mnemonic &&
      chainType === "ethereum"
    ) {
      setAccountData(mnemonic, result.address);
    }
  }, [result, mnemonic, chainType, setAccountData]);

  const selectedChain = CHAIN_TYPE_OPTIONS.find(
    (opt) => opt.value === chainType,
  );

  return (
    <div className="component">
      <h3>Multichain Address Generator</h3>
      <p>Generate addresses from mnemonic phrases for multiple blockchain networks</p>

      <div className="form-group">
        <label htmlFor="chainType">Chain Type:</label>
        <select
          id="chainType"
          value={chainType}
          onChange={(e) => setChainType(e.target.value as ChainType)}
          style={{ marginBottom: "0.5rem" }}
        >
          {CHAIN_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label} - {option.description}
            </option>
          ))}
        </select>
        {selectedChain && (
          <div
            style={{
              fontSize: "0.85rem",
              color: "rgba(255, 255, 255, 0.7)",
              marginTop: "0.25rem",
              padding: "0.5rem",
              background: "rgba(0, 0, 0, 0.2)",
              borderRadius: "4px",
            }}
          >
            <strong>Example:</strong> {selectedChain.example}
          </div>
        )}
      </div>

      {/* Show network toggle for Bitcoin chains */}
      {(chainType.startsWith("bitcoin") || chainType === "solana") && (
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={isMainnet}
              onChange={(e) => setIsMainnet(e.target.checked)}
              style={{ marginRight: "0.5rem", cursor: "pointer" }}
            />
            {isMainnet ? "Mainnet" : "Testnet"}
            <span
              style={{
                fontSize: "0.85rem",
                color: "rgba(255, 255, 255, 0.7)",
                marginLeft: "0.5rem",
              }}
            >
              ({isMainnet ? "Production network" : "Test network"})
            </span>
          </label>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="mnemonic">Mnemonic Phrase:</label>
        <textarea
          id="mnemonic"
          value={mnemonic}
          onChange={(e) => setMnemonic(e.target.value)}
          placeholder="Enter your mnemonic phrase..."
          rows={3}
        />
      </div>

      <div className="form-group">
        <label htmlFor="addressIndex">Address Index:</label>
        <input
          id="addressIndex"
          type="number"
          value={addressIndex}
          onChange={(e) => setAddressIndex(parseInt(e.target.value) || 0)}
          min="0"
          max="1000"
        />
        <div
          style={{
            fontSize: "0.85rem",
            color: "rgba(255, 255, 255, 0.7)",
            marginTop: "0.25rem",
          }}
        >
          Derivation path index (0 = first address, 1 = second address, etc.)
        </div>
      </div>

      <button
        className="button"
        onClick={generateAddress}
        disabled={loading || !isReady}
        style={{ width: "100%", margin: "0" }}
      >
        {loading
          ? "Generating..."
          : `Generate ${selectedChain?.label || "Address"}`}
      </button>

      {result && (
        <div className={`output ${result.success ? "success" : "error"}`}>
          {result.success ? (
            <>
              <div style={{ marginBottom: "1rem" }}>
                <strong style={{ fontSize: "1.1rem", display: "block", marginBottom: "0.5rem" }}>
                  Generated Address:
                </strong>
                <div
                  style={{
                    fontFamily: "Monaco, Menlo, Ubuntu Mono, monospace",
                    fontSize: "0.95rem",
                    wordBreak: "break-all",
                    padding: "0.75rem",
                    background: "rgba(0, 0, 0, 0.3)",
                    borderRadius: "4px",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  {result.address}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  gap: "0.5rem 1rem",
                  fontSize: "0.9rem",
                }}
              >
                <strong>Chain Type:</strong>
                <span>
                  {selectedChain?.label || (result as any).chainType || "Ethereum"}
                </span>

                <strong>Network:</strong>
                <span>
                  {(result as any).isMainnet !== undefined
                    ? (result as any).isMainnet
                      ? "Mainnet"
                      : "Testnet"
                    : "Mainnet (default)"}
                </span>

                <strong>Index:</strong>
                <span>{result.index ?? 0}</span>

                {"mnemonic" in result && result.mnemonic && (
                  <>
                    <strong>Mnemonic:</strong>
                    <span>{result.mnemonic}</span>
                  </>
                )}
              </div>

              {/* Show derivation path info */}
              <div
                style={{
                  marginTop: "1rem",
                  padding: "0.75rem",
                  background: "rgba(0, 0, 0, 0.2)",
                  borderRadius: "4px",
                  fontSize: "0.85rem",
                  color: "rgba(255, 255, 255, 0.8)",
                }}
              >
                <strong>Derivation Path:</strong>{" "}
                {chainType === "ethereum" && (
                  <code>m/44'/60'/0'/0/{addressIndex}</code>
                )}
                {chainType === "bitcoin_taproot" && (
                  <code>m/86'/0'/0'/0/{addressIndex}</code>
                )}
                {chainType === "bitcoin_segwit" && (
                  <code>m/84'/0'/0'/0/{addressIndex}</code>
                )}
                {chainType === "bitcoin_legacy" && (
                  <code>m/44'/0'/0'/0/{addressIndex}</code>
                )}
                {chainType === "bitcoin_nested_segwit" && (
                  <code>m/49'/0'/0'/0/{addressIndex}</code>
                )}
                {chainType === "solana" && (
                  <code>m/44'/501'/0'/{addressIndex}'</code>
                )}
              </div>
            </>
          ) : (
            <>
              <strong style={{ display: "block", marginBottom: "0.5rem" }}>
                Error:
              </strong>
              <div style={{ color: "rgba(255, 107, 107, 0.9)" }}>
                {result.error}
              </div>
            </>
          )}
        </div>
      )}

      {!isReady && (
        <div
          style={{
            marginTop: "1rem",
            padding: "0.75rem",
            background: "rgba(255, 193, 7, 0.1)",
            border: "1px solid rgba(255, 193, 7, 0.3)",
            borderRadius: "8px",
            color: "rgba(255, 193, 7, 0.9)",
            fontSize: "0.9rem",
          }}
        >
          ⚠️ WASM module is loading. Please wait...
        </div>
      )}
    </div>
  );
}

export default AddressGenerator;
